import { X } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

export type ProjectTab = {
  id: string
  name: string
  /** зелёная точка — проект сейчас в работе */
  running?: boolean
}

type Props = {
  tabs: ProjectTab[]
  activeId: string | null
  onSelect: (id: string) => void
  onClose: (id: string) => void
}

/**
 * Полоска «открытых проектов» (как вкладки браузера / FastPanel).
 * Видна на всех страницах; крестик убирает из списка быстрых ссылок.
 */
export default function ProjectTabsBar({ tabs, activeId, onSelect, onClose }: Props) {
  if (!tabs.length) return null

  return (
    <TooltipProvider delayDuration={400}>
      <div
        className="shrink-0 flex items-stretch gap-0.5 px-1 md:px-2 py-0.5 rounded-lg bg-muted/50 border border-border overflow-x-auto overflow-y-hidden min-w-0 scrollbar-thin"
        role="tablist"
        aria-label="Открытые проекты"
      >
        {tabs.map((tab) => {
          const active = tab.id === activeId
          const label = tab.name || tab.id
          return (
            <div
              key={tab.id}
              role="tab"
              aria-selected={active}
              className={`
                group relative flex items-center gap-1.5 shrink-0 max-w-[180px] md:max-w-[220px]
                px-2.5 py-1.5 text-[12px] md:text-[13px] cursor-pointer select-none
                transition-colors border-b-2
                ${active
                  ? 'text-foreground font-medium border-primary bg-background/60'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-background/40'}
              `}
              onClick={() => { if (!active) onSelect(tab.id) }}
              onAuxClick={(e) => {
                // Средняя кнопка мыши — закрыть (как в браузере)
                if (e.button === 1) {
                  e.preventDefault()
                  onClose(tab.id)
                }
              }}
            >
              {tab.running && (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-success shrink-0"
                  title="В работе"
                />
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate leading-tight">{label}</span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs max-w-xs">
                  {label}
                </TooltipContent>
              </Tooltip>
              <button
                type="button"
                title="Закрыть вкладку"
                aria-label={`Закрыть «${label}»`}
                className={`
                  shrink-0 flex items-center justify-center w-4 h-4 rounded-sm
                  text-muted-foreground/70 hover:text-foreground hover:bg-muted
                  transition-opacity
                  ${active ? 'opacity-70' : 'opacity-0 group-hover:opacity-70 focus:opacity-70'}
                `}
                onClick={(e) => {
                  e.stopPropagation()
                  onClose(tab.id)
                }}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )
        })}
      </div>
    </TooltipProvider>
  )
}

const MAX_TABS = 12

function storageKey(product: string): string {
  return product === 'profiler' ? 'cb-project-tabs-profiler' : 'cb-project-tabs-booster'
}

export function loadProjectTabs(product: string): ProjectTab[] {
  try {
    const raw = localStorage.getItem(storageKey(product))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((t: any) => t && typeof t.id === 'string')
      .map((t: any) => ({ id: t.id, name: String(t.name || t.id) }))
      .slice(0, MAX_TABS)
  } catch {
    return []
  }
}

export function saveProjectTabs(product: string, tabs: ProjectTab[]) {
  try {
    localStorage.setItem(
      storageKey(product),
      JSON.stringify(tabs.map(({ id, name }) => ({ id, name }))),
    )
  } catch { /* quota / private mode */ }
}

/** Добавить или обновить вкладку (порядок как в браузере — новые в конец). */
export function upsertProjectTab(
  tabs: ProjectTab[],
  id: string,
  name: string,
): ProjectTab[] {
  const existing = tabs.findIndex((t) => t.id === id)
  if (existing >= 0) {
    const next = [...tabs]
    if (name && next[existing].name !== name) {
      next[existing] = { ...next[existing], name }
    }
    return next
  }
  const next = [...tabs, { id, name: name || id }]
  if (next.length > MAX_TABS) {
    // Вытесняем самую старую слева
    return next.slice(next.length - MAX_TABS)
  }
  return next
}

/** Убрать вкладку; вернуть id соседа для переключения, если закрыли активную. */
export function closeProjectTab(
  tabs: ProjectTab[],
  id: string,
  activeId: string | null,
): { tabs: ProjectTab[]; switchTo: string | null } {
  const idx = tabs.findIndex((t) => t.id === id)
  if (idx < 0) return { tabs, switchTo: null }
  const next = tabs.filter((t) => t.id !== id)
  if (id !== activeId) return { tabs: next, switchTo: null }
  const neighbor = next[idx] || next[idx - 1] || null
  return { tabs: next, switchTo: neighbor?.id ?? null }
}
